import type { ReactNode } from 'react';
import { dueChip } from '@/components/tasks/task-due-chip';
import { Badge } from '@/components/ui';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import { taskStateLabel } from '@/lib/task-time';
import type { AccountLastTask } from '@/lib/types';
import {
  ACCOUNT_NO_TASKS,
  ACCOUNT_TASK_NOT_VISIBLE,
  accountLastTaskDue,
} from './account-last-task';

/*
 * The two account-wide "latest task" cells, the twins of `AccountLastNoteCell`.
 * Wording and the three-state rule live in `./account-last-task`.
 *
 * `undefined` (an older server) renders a bare dash with no claim; `null` (no
 * task on the account) renders "No tasks". They are different facts.
 *
 * The state cue is TaskList's: a box that is filled with a check when done and
 * empty otherwise, a struck-through title when closed, and the word
 * "Cancelled" on a cancelled one so the two closed states are told apart by
 * text rather than by the box alone.
 */

const DASH = '—';

interface CellProps {
  task: AccountLastTask | null | undefined;
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-2.5 w-2.5"
    >
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}

/** The TASK column: state box, title, then the due time and assignee. */
export function AccountLastTaskCell({
  task,
  viewerTimeZone,
}: CellProps & { viewerTimeZone: string }) {
  if (task === undefined) return <span className="text-fg-subtle">{DASH}</span>;
  if (task === null) return <span className="text-fg-subtle">{ACCOUNT_NO_TASKS}</span>;

  const closed = task.state !== 'open';
  const title = task.title?.trim() ?? '';
  const assignee = task.assignedTo?.name?.trim() || null;
  const chip = dueChip(accountLastTaskDue(task), {
    open: !closed,
    viewerTimeZone,
    assigneeName: assignee,
  });

  return (
    <div className="grid min-w-0 gap-0.5">
      <div className="flex min-w-0 items-center gap-1.5">
        <span
          aria-hidden="true"
          className={cx(
            'grid h-3.5 w-3.5 shrink-0 place-items-center rounded-xs border',
            task.state === 'done'
              ? 'border-success bg-success text-fg-on-accent'
              : 'border-hairline-strong bg-surface text-transparent',
          )}
        >
          <CheckIcon />
        </span>
        <span className="sr-only">{`${taskStateLabel(task.state)}:`}</span>
        {task.title === null ? (
          <span className="min-w-0 truncate text-fg-muted italic">{ACCOUNT_TASK_NOT_VISIBLE}</span>
        ) : (
          <span
            className={cx(
              'min-w-0 truncate',
              closed ? 'text-fg-muted line-through' : 'font-medium text-fg',
            )}
            title={title}
          >
            {title}
          </span>
        )}
        {task.state === 'cancelled' && (
          <Badge tone="warning" className="shrink-0">
            {taskStateLabel(task.state)}
          </Badge>
        )}
      </div>
      {(chip || assignee) && (
        <div className="flex min-w-0 items-center gap-1 pl-5 text-xs">
          {chip && (
            <span
              title={chip.title}
              className={cx(
                'shrink-0 whitespace-nowrap tabular-nums',
                chip.late ? 'font-medium text-danger' : 'text-fg-secondary',
              )}
            >
              {chip.text}
            </span>
          )}
          {chip && assignee && <span className="text-fg-subtle">·</span>}
          {assignee && <span className="min-w-0 truncate text-fg-secondary">{assignee}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * The task cells as a button that opens the page's one tasks-and-notes sheet,
 * styled exactly as `OpenNotesButton`. Offered on every state, "No tasks"
 * included: the sheet is also where the first task is added.
 */
export function OpenTasksButton({
  companyName,
  onOpen,
  children,
}: {
  companyName: string;
  onOpen: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={`Open tasks for ${companyName}`}
      onClick={onOpen}
      className="-mx-1 -my-1.5 block w-[calc(100%+0.5rem)] min-w-0 cursor-pointer rounded-sm px-1 py-1.5 text-left transition-colors duration-150 ease-out hover:bg-surface-hover motion-reduce:transition-none"
    >
      {children}
    </button>
  );
}

/**
 * The DATE column: when the task was added. Shown to every reader.
 *
 * ⚠️ Rendered in the reader's LOCAL time, while the `lastTaskAfter` /
 * `lastTaskBefore` filter compares UTC days. The exact instant is on the `title`.
 */
export function AccountLastTaskDateCell({ task }: CellProps) {
  if (task === undefined) return <span className="text-fg-subtle">{DASH}</span>;
  if (task === null) return <span className="text-fg-subtle">{ACCOUNT_NO_TASKS}</span>;
  return (
    <span className="tabular-nums text-fg-secondary" title={task.createdAt}>
      {formatDateTime(task.createdAt)}
    </span>
  );
}
